import {Plural, Trans} from '@lingui/macro';
import {difference, noop, union} from 'lodash';
import React, {FC, useEffect, useMemo, useState} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {ScrollDirectionsEnum} from '../../../../srcCommon/scroll/scrollerTypes';
import {useLinkNavigation} from '../../../components/common/navigation/useLinkNavigation';
import {
  computeMultiSelectCheckboxState,
  VirtualTableMultiSelectStatesEnum,
} from '../../../components/common/table/helpers/tableHelpers';
import {StaticVirtualTable} from '../../../components/common/table/staticVirtualTable';
import {TableMultiSelectHeaderRow} from '../../../components/common/table/tableMultiSelectHeaderRow';
import {TableRowRenderOptions} from '../../../components/common/table/tableRow';
import {VirtualTableRowSiblingData} from '../../../components/common/table/virtualTableBase';
import {buildSettingsPath} from '../../../models/url/settingsUrlHelpers';
import {SettingsResourcePagesEnum, SettingsSignaturesUrl} from '../../../models/url/settingsUrlModel';
import {AsyncMoreStatusesEnum, AsyncStatus, ResourceTableStatusesEnum} from '../../../store/appTypes';
import {SignaturesTableActions} from './signaturesTableActions';
import {signaturesTableColumns} from './signaturesTableConfig';
import {SignaturesTableRowContainer} from './signaturesTableRowContainer';

/*
 * Props.
 */

interface SignaturesTableProps {
  signaturesUrl: SettingsSignaturesUrl;
  loadingStatus: AsyncStatus;
  signatureIds: ReadonlyArray<number>;
  fetchSignaturesForSettingsTableAsync: HandlerOf<string>;
  markSignaturesAsStale: Handler;
}

/*
 * Style.
 */

const StyledTableContainerDiv = styled.div`
  flex: 1;
  overflow: hidden;
  position: relative;
`;

const StyledEmptyStateContainerDiv = styled.div`
  position: absolute;
  top: 80px;
  left: 0;
  right: 0;
  color: ${(p) => p.theme.greys.shade70};
  text-align: center;
`;

/*
 * Component.
 */

export const SignaturesTable: FC<SignaturesTableProps> = (props) => {
  const {
    signatureIds,
    signaturesUrl,
    loadingStatus,
    fetchSignaturesForSettingsTableAsync,
    markSignaturesAsStale,
  } = props;
  const [lastSelectedCardId, setLastSelectedCardId] = useState<number>();
  const [selectedSignatureIds, setSelectedSignatureIds] = useState<ReadonlyArray<number>>([]);
  const {navigateToLink} = useLinkNavigation();

  const multiSelectCheckboxState: VirtualTableMultiSelectStatesEnum = useMemo(
    () => computeMultiSelectCheckboxState(signatureIds.length, selectedSignatureIds.length),
    [signatureIds.length, selectedSignatureIds.length],
  );

  useEffect(() => {
    if (loadingStatus.isStale) {
      fetchSignaturesForSettingsTableAsync(signaturesUrl.namespace);
    }
  }, [fetchSignaturesForSettingsTableAsync, loadingStatus.isStale, signaturesUrl.namespace]);

  useEffect(() => setSelectedSignatureIds([]), [signaturesUrl.namespace]);

  const addSelectedSignatureIds = (signatureIdsToSelect: ReadonlyArray<number>) => {
    const updatedSelectedSignatureIds = union(selectedSignatureIds, signatureIdsToSelect);
    setSelectedSignatureIds(updatedSelectedSignatureIds);
  };

  const removeSelectedSignatureIds = (signatureIdsToRemove: ReadonlyArray<number>) => {
    const updatedSelectedSignatureIds = difference(selectedSignatureIds, signatureIdsToRemove);
    setSelectedSignatureIds(updatedSelectedSignatureIds);
  };

  const renderRow = (
    signatureId: number,
    options: TableRowRenderOptions,
    siblingData?: VirtualTableRowSiblingData<number>,
  ) => (
    <SignaturesTableRowContainer
      signatureId={signatureId}
      options={options}
      tableColumns={signaturesTableColumns}
      isSelected={selectedSignatureIds.includes(signatureId)}
      isInMultiSelectMode={selectedSignatureIds.length > 0}
      isNextSiblingSelected={selectedSignatureIds.includes(siblingData?.next ?? -1)}
      isPreviousSiblingSelected={selectedSignatureIds.includes(siblingData?.previous ?? -1)}
      onSelect={() => {
        const signatureEditUrl = buildSettingsPath({
          ...signaturesUrl,
          resourcePage: SettingsResourcePagesEnum.EDIT,
          signatureId,
        });

        navigateToLink(signatureEditUrl);
      }}
      onMultiSelect={() => {
        addSelectedSignatureIds(findSelectedValues(signatureId, lastSelectedCardId, signatureIds));
      }}
      onCheckBoxToggle={(shouldSelect) => {
        if (shouldSelect) {
          addSelectedSignatureIds([signatureId]);
          setLastSelectedCardId(signatureId);
        } else {
          removeSelectedSignatureIds([signatureId]);
        }
      }}
      maxSortNumber={signatureIds?.length}
      signatureIndex={signatureIds.indexOf(signatureId)}
    />
  );

  const renderMultiSelectHeader = (onMultiSelectChange: HandlerOf<boolean>) => (
    <TableMultiSelectHeaderRow
      multiSelectStatus={multiSelectCheckboxState}
      renderTotalItemsLabel={() => (
        <Plural
          id="signatures-table-total-selected"
          value={selectedSignatureIds.length}
          one="1 signature selected"
          other="# signatures selected"
        />
      )}
      onMultiSelectChange={onMultiSelectChange}
    >
      <SignaturesTableActions
        signatureIds={selectedSignatureIds}
        onSuccessfulDelete={() => {
          setSelectedSignatureIds([]);
          setLastSelectedCardId(undefined);
          markSignaturesAsStale();
        }}
      />
    </TableMultiSelectHeaderRow>
  );

  const renderEmptyState = () => (
    <StyledEmptyStateContainerDiv>
      <Trans id="signatures-table-empty-state">No signatures</Trans>
    </StyledEmptyStateContainerDiv>
  );

  return (
    <StyledTableContainerDiv>
      <StaticVirtualTable<number>
        columns={signaturesTableColumns}
        items={signatureIds}
        renderRow={renderRow}
        renderEmpty={renderEmptyState}
        renderMultiSelectHeader={renderMultiSelectHeader}
        shouldAllowMultiSelect
        isInMultiSelectMode={selectedSignatureIds.length > 0}
        onMultiSelectHeaderChange={(checked: boolean) => {
          if (checked) {
            addSelectedSignatureIds(signatureIds);
          } else {
            removeSelectedSignatureIds(signatureIds);
          }
        }}
        hasData={false}
        shouldRenderEmptyState={
          loadingStatus.status === ResourceTableStatusesEnum.LOADED && signatureIds.length === 0
        }
        moreStatuses={{
          [ScrollDirectionsEnum.TOP]: AsyncMoreStatusesEnum.ALL_LOADED,
          [ScrollDirectionsEnum.BOTTOM]: AsyncMoreStatusesEnum.ALL_LOADED,
        }}
        onLoadMore={noop}
      />
    </StyledTableContainerDiv>
  );
};
function findSelectedValues(
  signatureId: number,
  lastSelectedSignatureId: number | undefined,
  signatureIds: ReadonlyArray<number>,
): ReadonlyArray<number> {
  if (!lastSelectedSignatureId) {
    return [];
  }
  const currentIndex = signatureIds.indexOf(signatureId);
  const previousIndex = signatureIds.indexOf(lastSelectedSignatureId);

  if (currentIndex < previousIndex) {
    return signatureIds.slice(currentIndex, previousIndex);
  }
  return signatureIds.slice(previousIndex, currentIndex + 1);
}
